import type { InsightsSavedView, SavedViewFilters } from '#services/api/saved-views.ts';

import * as React from 'react';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeScalableIcon } from '@mend/mui';
import { Badge, Button, ButtonGroup } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Link, useNavigate } from 'react-router';

import { useMenu } from '#hooks/menu.ts';
import { savedViews } from '#lib/react-query/queries.ts';
import { SavedViewActionsMenu } from '#routes/Home/SavedViews/SavedViewActionsMenu.tsx';
import { updateSavedView } from '#services/api/saved-views.ts';
import { useFilters } from '../Filters/FiltersContext.tsx';
import { SavedViewDeleteDialog } from './SavedViewDeleteDialog.tsx';
import { SavedViewDialog } from './SavedViewDialog.tsx';

interface SavedViewButtonGroupProps {
  view: InsightsSavedView;
  isActive: boolean;
  getSerializedFilters: () => SavedViewFilters;
}

export function SavedViewButtonGroup(props: SavedViewButtonGroupProps) {
  const queryClient = useQueryClient();
  const menu = useMenu();
  const filters = useFilters();

  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (params: { id: number; filters: SavedViewFilters }) =>
      updateSavedView(params.id, { filters: params.filters }),
    onSuccess: async () => {
      enqueueSnackbar('View saved', { variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: savedViews.queryKey });
      filters.selectView(props.view.id);
      void navigate(`/?view=${props.view.id}`);
    },
    onError: () => {
      enqueueSnackbar('Failed to save view', { variant: 'error' });
    },
  });

  const [action, setAction] = React.useState<'rename' | 'delete' | null>(null);

  const isActive =
    filters.selectedViewState.mode === 'route'
      ? props.isActive
      : filters.selectedViewState.mode === 'view'
        ? filters.selectedViewState.viewId === props.view.id
        : false;
  const wasLastActive = props.view.id === filters.lastActiveViewId;
  const isDirty = !isActive && wasLastActive;
  const isHighlighted = isActive || wasLastActive;

  return (
    <>
      <Badge color="secondary" variant="dot" invisible={!isDirty}>
        <ButtonGroup
          variant={isHighlighted ? 'outlined' : 'text'}
          color={isHighlighted ? 'primary' : 'neutral'}
          aria-label={`${props.view.name}${isActive ? ' (Active)' : isDirty ? ' (Unsaved changes)' : ''}`}
        >
          <Button
            component={Link}
            to={{ pathname: '/', search: `?view=${props.view.id}` }}
            sx={{ '&&': { borderRight: 'none' } }}
            onClick={() => filters.selectView(props.view.id)}
          >
            {props.view.name}
          </Button>

          <Button
            {...menu.getTriggerProps()}
            sx={{ '&&': { paddingX: 1 } }}
            aria-label={`${props.view.name} actions`}
          >
            <FontAwesomeScalableIcon icon={faEllipsisVertical} />
          </Button>
        </ButtonGroup>
      </Badge>

      <SavedViewActionsMenu
        menu={menu}
        isDirty={isDirty}
        onSave={() =>
          mutation.mutate({
            id: props.view.id,
            filters: props.getSerializedFilters(),
          })
        }
        onRename={() => setAction('rename')}
        onDelete={() => setAction('delete')}
      />

      {action === 'rename' && <SavedViewDialog view={props.view} onClose={() => setAction(null)} />}

      {action === 'delete' && (
        <SavedViewDeleteDialog
          view={props.view}
          onDelete={() => {
            if (isActive) {
              void navigate(`/?tab=${props.view.tabType}`);
            }
          }}
          onClose={() => setAction(null)}
        />
      )}
    </>
  );
}
